
// @vendors
const React = require('react');
const PropTypes = require('prop-types');
const moment = require('moment');

// @helpers
const { formatText, FormattedText } = require('core/i18n').i18n;
const { formatAmount } = require('utilities/currencyHelper');
const DateHelper = require('utilities/dateHelper');
const { taggingEventHandled } = require('utilities/taggingEventHandled');

// @components
const StockExchangeIndexVariationBracket = require('components/broker/commons/stockExchange/StockExchangeIndexVariationBracket');

// @ commons
const { OKTable } = require('commonsComponents/OKTable');
const { OKTableRow } = require('commonsComponents/OKTableRow');
const { OKTableCell } = require('commonsComponents/OKTableCell');
const { OKButton } = require('components/commons/OKButton/OKButton');
const { LoadingSection } = require('commonsComponents/loadingSection');
const { OKLink } = require('commonsComponents/OKLink');
const { EmptyState } = require('commonsComponents/emptyState');
const Flag = require('components/broker/commons/flag/flag');

// @ constants
const {
    MYPROFILE_BROKER_EXPLORE_STOCKS_URL,
    BROKER_LARGE_FLAG_SIZE,
    HYPHEN,
    DATE_FORMAT_SLASHES_SHORT_DAY_MONTH_YEAR,
    DATE_FORMAT_DAY_MONTH_SHORT,
    BROKER_TYPE_STOCKS_NEW_DECIMALS
} = require('constants/index');
const {
    BROKER_STOCKS_DETAIL_VARIANT_TWO_LINES,
    BROKER_STOCKS_DETAIL_VARIANT_THREE_LINES,
    BROKER_STOCKS_LIST_PAGE_SIZE
} = require('constants/brokerKnowTheseStocks');

class BrokerKnowTheseStocksList extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            numberOfStocksToBeShown: BROKER_STOCKS_LIST_PAGE_SIZE
        }

        this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this);
        this.buildContent = this.buildContent.bind(this);
    }

    componentDidMount() {
        const { immBrokerStocks, stocksType } = this.props;
        const isFetching = immBrokerStocks.get('isFetching');

        if (!isFetching) {
            this.props.getStocksDetails(stocksType);
        }
    }

    buildEmptyState() {
        const primaryText = formatText('broker-knowTheseStocksEmptyState');
        const secondaryText = formatText('broker-knowTheseStocksEmptyStateSecondary');
        const linkText = formatText('broker-mainStocksGoToSearch');
        return (
            <EmptyState
                secondaryText={secondaryText}
                primaryText={primaryText}
                linkText={linkText}
                linkURL={MYPROFILE_BROKER_EXPLORE_STOCKS_URL}
                iconType="search-wealth" />
        );
    }

    buildErrorState() {
        const primaryText = formatText('broker-lostConnectionFirstLine');
        return (
            <EmptyState
                primaryText={primaryText}
                iconType="nointernet-wealth" />
        );
    }

    buildTable() {
        const { immBrokerStocks } = this.props;
        const { numberOfStocksToBeShown } = this.state;

        const immStocksResponseList = immBrokerStocks.get('stocksResponseList');
        const totalNumberOfStocks = immBrokerStocks.get('totalNumberOfStocks');

        const immStocksList = (numberOfStocksToBeShown < totalNumberOfStocks) ? immStocksResponseList.slice(0, numberOfStocksToBeShown) : immStocksResponseList;

        const tableBodyRows = immStocksList.map((immStock) => {
            const market = immStock.get('market');
            const countryCode = immStock.getIn(['country', 'id']);
            const quotation = formatAmount(immStock.get('last'), undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS);

            const updateDate = moment(immStock.get('tradeDate'), DATE_FORMAT_SLASHES_SHORT_DAY_MONTH_YEAR);
            const quoteDateIsToday = moment().isSame(updateDate, 'day');
            const formattedDate = quoteDateIsToday ? formatText('common-today') : DateHelper.buildFormattedDate(updateDate, DATE_FORMAT_DAY_MONTH_SHORT);

            const updateTime = immStock.get('tradeTime') || '';

            const buyPageurl = `/myprofile/broker/stocksBuy/${immStock.get('instrumentId')}/${immStock.get('miccode')}`;

            const handledInstrumentClick = (instrumentStr) => {
                taggingEventHandled(this.props.pageName, 'knowTheseStocksproductLink', instrumentStr);
            }

            const handledBuyClick = (instrumentStr) => {
                taggingEventHandled(this.props.pageName, 'knowTheseStocksBuyButton', instrumentStr);
                this.props.onHandleBuyClick(buyPageurl)
            }

            return (
                <OKTableRow key={immStock.get('key')} className="broker-know-these-stocks__table-row">
                    <OKTableCell className="broker-know-these-stocks__table-cell broker-know-these-stocks__table-cell--stock-detail">
                        <OKLink onClick={() => handledInstrumentClick(immStock.get('instrument'))} to={`/myprofile/broker/productFile/${immStock.get('instrumentId')}/${immStock.get('miccode')}`} extraClass="broker-know-these-stocks__stock-name">
                            {immStock.get('instrument')}
                        </OKLink>
                        <div className="broker-know-these-stocks__stock-detail-second-line">
                            <Flag countryCode={countryCode} pixelsSize={BROKER_LARGE_FLAG_SIZE} />
                            <span className="broker-know-these-stocks__stock-city">{market.get('CITY')}, </span>
                            <span className="broker-know-these-stocks__country-code">{countryCode} - </span>
                            <span className="broker-know-these-stocks__stock-code">{immStock.get('localCode')}</span>
                        </div>
                        {
                            this.props.lineVariant === BROKER_STOCKS_DETAIL_VARIANT_THREE_LINES ?
                                <div className="broker-know-these-stocks__stock-detail-third-line">
                                    {immStock.get('miccode')} - <FormattedText value="broker-isinLabel" injectedStrings={[immStock.get('instrumentId')]} />
                                </div>
                                :
                                ''
                        }
                    </OKTableCell>
                    <OKTableCell
                        alignment="right"
                        className="broker-know-these-stocks__table-cell broker-know-these-stocks__table-cell--stock-value"
                    >
                        <div className="broker-know-these-stocks__stock-value">
                            {quotation} {immStock.get('currencyCode', HYPHEN)}
                        </div>
                        <div className="broker-know-these-stocks__stock-variation">
                            <StockExchangeIndexVariationBracket
                                variationPts={formatAmount(immStock.get('netChangePrice'), undefined, BROKER_TYPE_STOCKS_NEW_DECIMALS)}
                                variationPerc={formatAmount(immStock.get('netChangePercentage'))}
                            />
                            <span className="broker-know-these-stocks__stock-variation-separator">|</span>
                            <span>{formattedDate}, {updateTime}</span>
                        </div>
                    </OKTableCell>
                    <OKTableCell
                        alignment="right"
                        className="broker-know-these-stocks__table-cell broker-know-these-stocks__table-cell--stock-action"
                    >
                        <div className="broker-know-these-stocks__buy-button">
                            <OKButton modifier="secondary" size="compact" onClick={() => handledBuyClick(immStock.get('instrument'))}>
                                <div className="broker-know-these-stocks__buy-button-label">
                                    <FormattedText value="broker-knowTheseStocksBuyButtonLabel" format="uppercase" />
                                </div>
                            </OKButton>
                        </div>
                    </OKTableCell>
                </OKTableRow>
            )
        });

        return (
            <div className="broker-know-these-stocks__table-wrapper">
                <OKTable className="broker-know-these-stocks__stocks-table">
                    <tbody>
                        {tableBodyRows}
                    </tbody>
                </OKTable>
            </div>
        );
    }

    handleLoadMoreClick() {
        this.setState({
            numberOfStocksToBeShown: this.state.numberOfStocksToBeShown + BROKER_STOCKS_LIST_PAGE_SIZE
        })
    }

    buildContent(numberOfStocks) {
        let content;
        let footer;

        let searchLink = (
            <OKLink to={MYPROFILE_BROKER_EXPLORE_STOCKS_URL} onClick={this.props.onHandleAdvancedSearchClick}>
                <FormattedText value="broker-advancedSearchLink" />
            </OKLink>
        );

        if (this.state.numberOfStocksToBeShown < numberOfStocks) {
            footer = (<div className="broker-know-these-stocks__load-more">
                {searchLink}
                <OKLink modifier="widget" inverse={true} onClick={this.handleLoadMoreClick}>
                    <FormattedText value="broker-knowTheseStocksLoadMore" />
                </OKLink>
            </div>)
        }
        else {
            footer = (<div className="broker-know-these-stocks__load-more">
                {searchLink}
            </div>)
        }
        content = this.buildTable();

        return (
            <div>
                {content}
                {footer}
            </div>
        )
    }

    render() {
        const { immBrokerStocks } = this.props;

        const isFetching = immBrokerStocks.get('isFetching');
        const isError = immBrokerStocks.get('error');
        const totalNumberOfStocks = immBrokerStocks.get('totalNumberOfStocks');

        if (!isFetching) {
            if (isError) {
                return this.buildErrorState();
            }
            if (totalNumberOfStocks) {
                return this.buildContent(totalNumberOfStocks);
            } else {
                return this.buildEmptyState();
            }
        }

        return (<LoadingSection />);
    }

}

BrokerKnowTheseStocksList.defaultProps = {
    lineVariant: BROKER_STOCKS_DETAIL_VARIANT_THREE_LINES
};

BrokerKnowTheseStocksList.propTypes = {
    immBrokerStocks: PropTypes.object.isRequired,
    getStocksDetails: PropTypes.func.isRequired,
    stocksType: PropTypes.string.isRequired,
    onHandleBuyClick: PropTypes.func.isRequired,
    lineVariant: PropTypes.oneOf([BROKER_STOCKS_DETAIL_VARIANT_TWO_LINES, BROKER_STOCKS_DETAIL_VARIANT_THREE_LINES]),
    onHandleAdvancedSearchClick: PropTypes.func.isRequired,
    pageName: PropTypes.string
}

module.exports.BrokerKnowTheseStocksList = BrokerKnowTheseStocksList;
